import { useCallback, useEffect, useRef } from 'react';

/**
 * Angular `search$.pipe(debounceTime(300), distinctUntilChanged())`: every call restarts the timer, and when it fires
 * the value is forwarded unless it equals the last value that was forwarded (the first value always goes through).
 * The caller trims the text before calling, exactly as the Angular `onSearch` handlers did.
 */
export function useDebouncedSearch(onValue: (text: string) => void, delayMs = 300): (text: string) => void {
  const callback = useRef(onValue);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const last = useRef<string | undefined>(undefined);

  useEffect(() => {
    callback.current = onValue;
  });

  useEffect(() => () => clearTimeout(timer.current), []);

  return useCallback(
    (text: string) => {
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        if (last.current === text) {
          return;
        }
        last.current = text;
        callback.current(text);
      }, delayMs);
    },
    [delayMs],
  );
}
